<template>
  <fts-dropdown-select
    append-to-body
    placement="bottom"
    prevent-overflow-alt-axis
    prevent-overflow-tether-off
    content-class="dropdown-menu dropdown-menu-w-md"
    :reference-id="referenceId"
    :reference-for="referenceFor"
    overlay
    class="fts-assign-design-dropdown"
    @show="onShow"
    @hide="onHide"
  > 
    <fts-tippy
      slot="toggle"
      trigger-class="assign-design-toggle"
      content="Assignees by default"
      :style="{'--toggle-height': toggleAvatarSize + 'px'}"
    >
      <template>
        <div class="assign-design-assignee-container" v-if="toggleAssignees.length">
          <template v-for="(x, index) in toggleAssignees">
            <fts-profile
              :user-name="x.username"
              :profile-photo-size="toggleAvatarSize"
              :profile-name="false"
              :key="x.username"
              v-if="x.type === 'profile'"
            />

            <div
              class="assign-design-assignee-count"
              :style="{ 'height': `${toggleAvatarSize}px`, 'width': `${toggleAvatarSize}px` }"
              :key="`count-${index}`"
              v-else
            >{{x.count}}</div>
          </template>
        </div>

        <div
          class="assign-design-toggle-icon"
          :style="{ 'height': `${toggleAvatarSize}px`, 'width': `${toggleAvatarSize}px` }"
          v-else
        >
          <i class="fts-icon-user-plus"></i>
        </div>
      </template>
    </fts-tippy>

    <template #menu>
      <div class="dropdown-form">
        <div class="fts-form-group">
          <fts-form-label placement="top">
            Assignees
          </fts-form-label>
          <fts-select
            v-model="formData.assignees"
            :options="getOptions"
            label="displayName"
            :reduce="(x) => x.username || x.userName"
            multiple
            :popper-reference-for="`${referenceId} ${referenceFor}`"
            comfort-design
          >
            <template #selected-option-container="{ option }">
              <fts-identity-display
                identity-type="user"
                :identity-username="option.username"
                :profile-photo-size="16"
              />
            </template>
            <template #option="{ option }">
              <fts-identity-display
                identity-type="user"
                :identity-username="option.username"
                :profile-photo-size="16"
              />
            </template>
          </fts-select>
        </div>
      </div>
    </template>
  </fts-dropdown-select>
</template>

<script>
import changesets from 'diff-json';

import FTSDropdownSelect from '../elements/FTSDropdownSelect';
import FTSFormLabel from '../elements/FTSFormLabel';
import FTSIdentityDisplay from '../elements/FTSIdentityDisplay';
import FTSProfile from '../elements/FTSProfile';
import FTSSelect from '../elements/FTSSelect';
import FTSTippy from '../elements/FTSTippy';

export default {
  components: {
    'fts-dropdown-select': FTSDropdownSelect,
    'fts-form-label': FTSFormLabel,
    'fts-identity-display': FTSIdentityDisplay,
    'fts-profile': FTSProfile,
    'fts-select': FTSSelect,
    'fts-tippy': FTSTippy
  },
  props: {
    assignees: Array,
    users: Array,
    toggleMaxLength: {
      type: Number,
      default: 5
    },
    toggleAvatarSize: {
      type: Number,
      default: 20
    },
    referenceFor: String
  },
  data: () => ({
    originalData: {},
    formData: {}
  }),
  methods: {
    onShow() {
      this.formData = {
        assignees: Array.isArray(this.assignees) ? [...this.assignees] : []
      };
      this.originalData = _.cloneDeep(this.formData);
    },
    onHide() {
      const diff = changesets.diff(this.formData, this.originalData);
      if (diff && diff.length) {
        this.$emit('update', this.formData);
      }
      Object.assign(this.$data, this.$options.data.apply(this));
    }
  },
  computed: {
    getOptions() {
      const users = (Array.isArray(this.users) ? [...this.users] : [])
        .filter((x) => !x.disable)
        .reduce((result, x) => {
          if (this.formData.assignees.some((y) => y === x.username || y === x.userName)) {
            result.selected.push(x);
          } else {
            result.available.push(x);
          }
          return result;
        }, { available: [], selected: [] });

      return [
        ..._.orderBy(users.available, 'displayName', 'asc'),
        ..._.orderBy(users.selected, 'displayName', 'asc')
      ];
    },
    toggleAssignees() {
      const assignees = this.assignees || [];
      if (this.toggleMaxLength && (assignees.length > this.toggleMaxLength)) {
        const count = assignees.slice(this.toggleMaxLength - 1, assignees.length).length; 
        return assignees
          .slice(0, this.toggleMaxLength - 1)
          .map((x) => ({type: 'profile', username: x}))
          .concat({
            type: 'count',
            count: count > 99 ? 99 : count
          });
      } else {
        return assignees.map((x) => ({ type: 'profile', username: x }));
      }
    }
  },
  created() {
    this.referenceId = `inistate-ref-${this._uid}`;
  }
}
</script>

<style lang="scss" scoped>
@use "sass:color";
@use "./src/assets/scss/colors" as *;

.fts-assign-design-dropdown {
  .assign-design-toggle {
    display: flex;
    align-items: center;
  }
  .assign-design-assignee-container {
    display: flex;
    align-items: center;
    > *:not(:first-child) {
      margin-left: calc(var(--toggle-height) / 5 * -1);
    }
  }
  .assign-design-assignee-count {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 10em;
    @include theme {
      background-color: color.adjust(theme-get('fts-primary'), $lightness: 40%);
    }
    color: var(--fts-primary);
    font-size: 0.75rem;
  }
  .assign-design-toggle-icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.875rem;
    color: var(--fts-default);
  }
}
</style>